import { useMemo, useState } from "react";
import { Mail, MessageCircle, Phone, MapPin, Clock, CheckCircle2, Copy, Check, ChevronDown, Loader2 } from "lucide-react";
import Button from "../components/ui/Button";
import api from "../lib/api";

const CHANNELS = [
  {
    icon: MessageCircle,
    title: "WhatsApp",
    detail: "+250 783 325 156",
    href: "https://wa.me/250783325156",
    cta: "Chat with us",
    copyValue: "+250783325156",
  },
  {
    icon: Mail,
    title: "Email",
    detail: "support@shuwa.app",
    href: "mailto:support@shuwa.app",
    cta: "Send an email",
    copyValue: "support@shuwa.app",
  },
  {
    icon: Phone,
    title: "Phone",
    detail: "+250 783 325 156",
    href: "tel:+250783325156",
    cta: "Call support",
    copyValue: "+250783325156",
  },
];

const FAQS = [
  {
    q: "How fast will I hear back?",
    a: "Our team replies to WhatsApp and email within a few hours during business hours, and always within one business day.",
  },
  {
    q: "I run a business — how do I get listed?",
    a: "Head to the List your business page to start your application, or mention it in your message and we'll set you up.",
  },
  {
    q: "I have an issue with a booking or payment.",
    a: "Include your booking reference in the message below and our support team will look into it right away.",
  },
];

// Support hours in Kigali local time (CAT, UTC+2)
const HOURS = [
  { label: "Monday – Friday", days: [1, 2, 3, 4, 5], open: 8, close: 20 },
  { label: "Saturday", days: [6], open: 9, close: 17 },
  { label: "Sunday", days: [0], open: null, close: null },
];

function useOpenStatus() {
  return useMemo(() => {
    const now = new Date();
    const catHour = (now.getUTCHours() + 2) % 24;
    const day = (now.getUTCDay() + Math.floor((now.getUTCHours() + 2) / 24)) % 7;
    const today = HOURS.find((h) => h.days.includes(day));
    if (!today || today.open === null) return { open: false, label: "Closed today" };
    const isOpen = catHour >= today.open && catHour < today.close;
    return {
      open: isOpen,
      label: isOpen ? `Open until ${today.close}:00` : "Closed now",
    };
  }, []);
}

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable, ignore
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={`Copy ${label}`}
      className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function FaqItem({ q, a, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/10 pb-4 last:border-b-0 last:pb-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={open}
      >
        <span className="text-sm font-medium text-white">{q}</span>
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-all duration-200 ease-out ${open ? "mt-2 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden">
          <p className="text-sm leading-relaxed text-slate-400">{a}</p>
        </div>
      </div>
    </div>
  );
}

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [confirmationSent, setConfirmationSent] = useState(false);
  const status = useOpenStatus();

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((err) => ({ ...err, [name]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) nextErrors.email = "Enter a valid email address";
    if (!form.message.trim()) nextErrors.message = "Tell us a bit about how we can help";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const { data } = await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject || null,
        message: form.message.trim(),
      });
      setConfirmationSent(Boolean(data?.confirmation_sent));
      setSubmitted(true);
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors;
      if (err?.response?.status === 422 && fieldErrors) {
        setErrors(Object.fromEntries(Object.entries(fieldErrors).map(([field, msgs]) => [field, msgs[0]])));
      } else if (err?.response?.status === 429) {
        setSubmitError("You've sent a few messages already. Please wait a little and try again.");
      } else {
        setSubmitError(
          err?.response?.data?.message ||
            "We couldn't send your message. Please try again, or email support@shuwa.app."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border bg-gradient-to-b from-secondary/10 to-white px-4 py-16 sm:px-6">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/10 blur-3xl"
        />
        <div className="relative mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
            Contact us
          </span>
          <h1 className="mt-4 font-montserrat text-3xl font-bold text-slate-900 sm:text-4xl">
            We're here to help
          </h1>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            Questions about a booking, listing your business, or just want to say hi? Reach the SHUWA team
            through WhatsApp, email, or the form below.
          </p>
          <div className="mt-5 flex items-center justify-center gap-2 text-sm">
            <span className="relative flex h-2 w-2">
              {status.open && (
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              )}
              <span
                className={`relative inline-flex h-2 w-2 rounded-full ${status.open ? "bg-emerald-500" : "bg-slate-400"}`}
              />
            </span>
            <span className={`font-medium ${status.open ? "text-emerald-700" : "text-slate-500"}`}>
              {status.label}
            </span>
          </div>
        </div>
      </section>

      <section className="px-4 py-12 sm:px-6">
        <div className="mx-auto grid max-w-5xl gap-6 sm:grid-cols-3">
          {CHANNELS.map(({ icon: Icon, title, detail, href, cta, copyValue }) => (
            <a
              key={title}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="group relative rounded-2xl border border-border bg-white p-6 transition hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md"
            >
              <div className="flex items-center">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <CopyButton value={copyValue} label={title} />
              </div>
              <h3 className="mt-4 font-montserrat text-lg font-semibold text-slate-900">{title}</h3>
              <p className="mt-1 text-sm text-slate-600">{detail}</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                {cta}
                <span className="transition group-hover:translate-x-0.5">→</span>
              </span>
            </a>
          ))}
        </div>
      </section>

      <section className="px-4 pb-16 sm:px-6">
        <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-2xl border border-border bg-white p-6 sm:p-8">
            <h2 className="font-montserrat text-xl font-bold text-slate-900">Send us a message</h2>
            <p className="mt-1 text-sm text-slate-600">
              Fill in the form and our team will get back to you within one business day.
            </p>

            {submitted ? (
              <div
                role="status"
                aria-live="polite"
                className="mt-8 flex flex-col items-center justify-center gap-3 rounded-xl bg-emerald-50 py-12 text-center"
              >
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                <h3 className="font-montserrat text-lg font-semibold text-slate-900">Message sent</h3>
                <p className="max-w-sm text-sm text-slate-600">
                  Thanks, {form.name.split(" ")[0]}! We've received your message and will reply to{" "}
                  {form.email} shortly.
                  {confirmationSent && " We've also emailed you a confirmation."}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ name: "", email: "", subject: "", message: "" });
                  }}
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="mt-6 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Full name
                    </label>
                    <input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={onChange}
                      placeholder="Jane Doe"
                      aria-invalid={Boolean(errors.name)}
                      className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                        errors.name ? "border-red-300" : "border-border"
                      }`}
                    />
                    {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={onChange}
                      placeholder="you@example.com"
                      aria-invalid={Boolean(errors.email)}
                      className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                        errors.email ? "border-red-300" : "border-border"
                      }`}
                    />
                    {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Subject
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={form.subject}
                    onChange={onChange}
                    className="w-full rounded-lg border border-border px-3.5 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">General inquiry</option>
                    <option value="booking">Booking or payment issue</option>
                    <option value="business">List my business</option>
                    <option value="partnership">Partnership</option>
                    <option value="press">Press</option>
                  </select>
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700">
                      Message
                    </label>
                    <span className="text-xs text-slate-400">{form.message.length}/1000</span>
                  </div>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={1000}
                    value={form.message}
                    onChange={onChange}
                    placeholder="How can we help?"
                    aria-invalid={Boolean(errors.message)}
                    className={`w-full resize-none rounded-lg border px-3.5 py-2.5 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 ${
                      errors.message ? "border-red-300" : "border-border"
                    }`}
                  />
                  {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
                </div>

                {submitError && (
                  <p role="alert" className="rounded-lg bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
                    {submitError}
                  </p>
                )}

                <Button type="submit" size="lg" className="w-full sm:w-auto" disabled={submitting}>
                  {submitting ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </span>
                  ) : (
                    "Send message"
                  )}
                </Button>
              </form>
            )}
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-border bg-white p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Clock className="h-5 w-5" />
                </div>
                <h3 className="font-montserrat text-base font-semibold text-slate-900">Support hours</h3>
              </div>
              <dl className="mt-4 space-y-2 text-sm text-slate-600">
                <div className="flex justify-between">
                  <dt>Monday – Friday</dt>
                  <dd className="font-medium text-slate-900">8:00 – 20:00</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Saturday</dt>
                  <dd className="font-medium text-slate-900">9:00 – 17:00</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Sunday</dt>
                  <dd className="font-medium text-slate-900">Closed</dd>
                </div>
              </dl>
            </div>

            <a
              href="https://maps.google.com/?q=KN+1+Rd,+9+KN+89+St,+Kigali,+Rwanda"
              target="_blank"
              rel="noreferrer"
              className="group block overflow-hidden rounded-2xl border border-border bg-white transition hover:border-primary/40 hover:shadow-md"
            >
              <div className="relative h-28 w-full overflow-hidden bg-gradient-to-br from-primary/15 via-secondary/10 to-primary/5">
                <svg
                  className="absolute inset-0 h-full w-full opacity-40"
                  aria-hidden
                  viewBox="0 0 200 100"
                  preserveAspectRatio="none"
                >
                  <path d="M0 70 Q50 20 100 60 T200 40" stroke="currentColor" strokeWidth="1.5" fill="none" className="text-primary" />
                  <path d="M0 30 Q60 80 120 30 T200 70" stroke="currentColor" strokeWidth="1.5" fill="none" className="text-primary" />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white shadow-lg transition group-hover:scale-110">
                    <MapPin className="h-5 w-5" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-montserrat text-base font-semibold text-slate-900">Kigali, Rwanda</h3>
                <p className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-400">EAT (UTC+3)</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                  KN 1 Rd, 9 KN 89 St<br />
                  Kigali, Rwanda
                </p>
                <span className="mt-3 inline-block text-sm font-semibold text-primary group-hover:underline">
                  Get directions →
                </span>
              </div>
            </a>

            <div className="rounded-2xl bg-slate-950 p-6 text-white">
              <h3 className="font-montserrat text-base font-semibold">Frequently asked</h3>
              <div className="mt-4 space-y-4">
                {FAQS.map((f, i) => (
                  <FaqItem key={f.q} q={f.q} a={f.a} defaultOpen={i === 0} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
